import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Match, ScorerStanding } from '../../models/match';
import { PlayerRegistryEntry, Team } from '../../models/team';
import { GroupStandings, TournamentGroup } from '../../models/tournament';
import { AdminRole, AuthService } from '../../services/auth.service';
import { MatchService } from '../../services/match.service';
import { PlayerRegistryService } from '../../services/player-registry.service';
import { SystemStatusService } from '../../services/system-status.service';
import { TeamService } from '../../services/team.service';
import { TournamentService } from '../../services/tournament.service';

@Component({
  standalone: false,
  selector: 'app-admin-teams-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Squadre</h2>
      </div>
    </section>

    <p class="error-message" *ngIf="loaded && !canManageTournament()">Sezione disponibile per super user e organizzatore.</p>
    <div class="admin-page-grid" *ngIf="canManageTournament()">
      <app-team-admin [teams]="teams" [registryPlayers]="players" (changed)="refresh()"></app-team-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminTeamsPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly teamService = new TeamService();
  private readonly playerRegistryService = new PlayerRegistryService();

  role: AdminRole | null = null;
  teams: Team[] = [];
  players: PlayerRegistryEntry[] = [];
  loaded = false;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, teams, players] = await Promise.all([
        this.authService.me(),
        this.teamService.list(),
        this.playerRegistryService.list()
      ]);
      this.role = authState.role ?? null;
      this.teams = teams;
      this.players = players;
      this.loaded = true;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  canManageTournament(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-players-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Giocatori</h2>
      </div>
    </section>

    <p class="error-message" *ngIf="loaded && !canManageTournament()">Sezione disponibile per super user e organizzatore.</p>
    <div class="admin-page-grid" *ngIf="canManageTournament()">
      <app-player-registry-admin [players]="players" (changed)="refresh()"></app-player-registry-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminPlayersPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly playerRegistryService = new PlayerRegistryService();

  role: AdminRole | null = null;
  players: PlayerRegistryEntry[] = [];
  loaded = false;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, players] = await Promise.all([
        this.authService.me(),
        this.playerRegistryService.list()
      ]);
      this.role = authState.role ?? null;
      this.players = players;
      this.loaded = true;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  canManageTournament(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-groups-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Gestione gironi</h2>
      </div>
    </section>

    <p class="error-message" *ngIf="loaded && !canManageTournament()">Sezione disponibile per super user e organizzatore.</p>
    <div class="admin-page-grid" *ngIf="canManageTournament()">
      <app-group-admin [teams]="teams" [groups]="groups" (changed)="refresh()"></app-group-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminGroupsPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly teamService = new TeamService();
  private readonly tournamentService = new TournamentService();

  role: AdminRole | null = null;
  teams: Team[] = [];
  groups: TournamentGroup[] = [];
  loaded = false;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, teams, groups] = await Promise.all([
        this.authService.me(),
        this.teamService.list(),
        this.tournamentService.listGroups()
      ]);
      this.role = authState.role ?? null;
      this.teams = teams;
      this.groups = groups;
      this.loaded = true;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  canManageTournament(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-matches-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Calendario partite</h2>
      </div>
    </section>

    <div class="admin-page-grid">
      <app-match-admin [teams]="teams" [groups]="groups" [matches]="matches" [role]="role" (changed)="refresh()"></app-match-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminMatchesPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly teamService = new TeamService();
  private readonly matchService = new MatchService();
  private readonly tournamentService = new TournamentService();

  role: AdminRole | null = null;
  teams: Team[] = [];
  groups: TournamentGroup[] = [];
  matches: Match[] = [];
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, teams, groups, matches] = await Promise.all([
        this.authService.me(),
        this.teamService.list(),
        this.tournamentService.listGroups(),
        this.matchService.list()
      ]);
      this.role = authState.role ?? null;
      this.teams = teams;
      this.groups = groups;
      this.matches = matches;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-live-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Partita live</h2>
      </div>
      <a routerLink="/" target="_blank" rel="noreferrer">Apri schermo pubblico</a>
    </section>

    <div class="admin-page-grid">
      <app-live-admin [liveMatch]="liveMatch" [teams]="teams" (changed)="refresh()"></app-live-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminLivePageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly teamService = new TeamService();
  private readonly matchService = new MatchService();

  teams: Team[] = [];
  liveMatch: Match | null = null;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [teams, liveMatch] = await Promise.all([
        this.teamService.list(),
        this.matchService.live()
      ]);
      this.teams = teams;
      this.liveMatch = liveMatch;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-news-page',
  template: `
    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>News</h2>
      </div>
    </section>

    <div class="admin-page-grid">
      <app-news-admin></app-news-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminNewsPageComponent {}

@Component({
  standalone: false,
  selector: 'app-admin-standings-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Classifiche</h2>
      </div>
    </section>

    <div class="admin-page-grid">
      <app-standings-admin
        [standings]="standings"
        [scorers]="scorers"
        [canGenerateKnockout]="canManageTournament()"
        (changed)="refresh()"
      ></app-standings-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminStandingsPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly matchService = new MatchService();
  private readonly tournamentService = new TournamentService();

  role: AdminRole | null = null;
  standings: GroupStandings[] = [];
  scorers: ScorerStanding[] = [];
  loaded = false;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, standings, scorers] = await Promise.all([
        this.authService.me(),
        this.tournamentService.standings(),
        this.matchService.scorers()
      ]);
      this.role = authState.role ?? null;
      this.standings = standings;
      this.scorers = scorers;
      this.loaded = true;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  canManageTournament(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-knockout-page',
  template: `
    <app-loading-overlay [show]="isLoading"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Fase finale</h2>
      </div>
    </section>

    <p class="error-message" *ngIf="loaded && !canManageTournament()">Sezione disponibile per super user e organizzatore.</p>
    <div class="admin-page-grid" *ngIf="canManageTournament()">
      <app-knockout-admin [matches]="knockoutMatches"></app-knockout-admin>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminKnockoutPageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly tournamentService = new TournamentService();

  role: AdminRole | null = null;
  knockoutMatches: Match[] = [];
  loaded = false;
  isLoading = true;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, knockoutMatches] = await Promise.all([
        this.authService.me(),
        this.tournamentService.knockout()
      ]);
      this.role = authState.role ?? null;
      this.knockoutMatches = knockoutMatches;
      this.loaded = true;
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  canManageTournament(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }
}

@Component({
  standalone: false,
  selector: 'app-admin-maintenance-page',
  template: `
    <app-loading-overlay [show]="isLoading || isBulkActionRunning || isPublicStatusSaving"></app-loading-overlay>

    <section class="admin-intro">
      <div>
        <p class="eyebrow">Pannello amministrativo</p>
        <h2>Operazioni massive</h2>
      </div>
    </section>

    <p class="error-message" *ngIf="errorMessage">{{ errorMessage }}</p>
    <p class="success-message" *ngIf="successMessage">{{ successMessage }}</p>
    <p class="loading-message" *ngIf="isBulkActionRunning || isPublicStatusSaving">Operazione in corso, attendere...</p>
    <p class="error-message" *ngIf="loaded && !canUseMassiveActions()">Sezione riservata al super user.</p>

    <div class="admin-page-grid" *ngIf="canUseMassiveActions()">
      <section class="card system-status-card">
        <div class="section-heading">
          <div>
            <h2>Sistema pubblico</h2>
            <p class="hint">{{ publicOffline ? 'Offline: il pubblico vede la pagina coming soon.' : 'Online: il pubblico vede live, news e classifiche.' }}</p>
          </div>
          <button type="button" [class.danger]="!publicOffline" [class.secondary]="publicOffline" (click)="togglePublicOffline()" [disabled]="isPublicStatusSaving">
            {{ isPublicStatusSaving ? 'Salvataggio...' : (publicOffline ? 'Riattiva pubblico' : 'Metti offline') }}
          </button>
        </div>
      </section>

      <details class="card accordion-card maintenance-card" open>
        <summary>
          <span>Azzeramenti e cancellazioni</span>
          <small>Operazioni irreversibili</small>
        </summary>
        <div class="accordion-body bulk-actions">
          <button type="button" class="secondary" (click)="resetAllMatches()" [disabled]="isBulkActionRunning">Azzera tutte le partite</button>
          <button type="button" class="danger" (click)="deleteGroupMatches()" [disabled]="isBulkActionRunning">Cancella partite gironi</button>
          <button type="button" class="danger" (click)="deleteKnockoutMatches()" [disabled]="isBulkActionRunning">Cancella fase finale</button>
          <button type="button" class="danger" (click)="deleteAllGroups()" [disabled]="isBulkActionRunning">Cancella tutti i gironi</button>
          <button type="button" class="danger" (click)="deleteAllTeams()" [disabled]="isBulkActionRunning">Cancella tutte le squadre</button>
          <button type="button" class="danger" (click)="deleteAllPlayerRegistry()" [disabled]="isBulkActionRunning">Cancella anagrafiche giocatori</button>
        </div>
      </details>
    </div>
  `,
  styles: [".admin-intro {\n  align-items: center;\n  display: flex;\n  justify-content: space-between;\n  gap: 1rem;\n  margin-bottom: 1.25rem;\n}\n\n.admin-intro h2 {\n  font-size: 2rem;\n  margin: 0;\n}\n\n.admin-intro a {\n  background: linear-gradient(135deg, #ffd000, #eda900);\n  border-radius: 12px;\n  color: #080808;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n  text-decoration: none;\n}\n\n.eyebrow {\n  color: #20db60;\n  font-size: 0.8rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  margin: 0 0 0.25rem;\n  text-transform: uppercase;\n}\n\n.grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.3fr);\n}\n\n.admin-page-grid {\n  display: grid;\n  gap: 1.25rem;\n  grid-template-columns: minmax(0, 1fr);\n}\n\napp-live-admin {\n  grid-column: 1 / -1;\n}\n\n.maintenance-card,\napp-standings-admin,\napp-knockout-admin {\n  grid-column: 1 / -1;\n}\n\n.error-message {\n  background: rgba(220, 38, 38, 0.16);\n  border: 1px solid rgba(248, 113, 113, 0.4);\n  border-radius: 8px;\n  color: #fecaca;\n  padding: 0.9rem 1rem;\n}\n\n.success-message {\n  background: rgba(34, 197, 94, 0.16);\n  border: 1px solid rgba(74, 222, 128, 0.35);\n  border-radius: 8px;\n  color: #bbf7d0;\n  padding: 0.9rem 1rem;\n}\n\n.bulk-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n@media (max-width: 960px) {\n  .admin-intro {\n    align-items: flex-start;\n    flex-direction: column;\n  }\n\n  .grid {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class AdminMaintenancePageComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly authService = new AuthService();
  private readonly matchService = new MatchService();
  private readonly playerRegistryService = new PlayerRegistryService();
  private readonly systemStatusService = new SystemStatusService();
  private readonly teamService = new TeamService();
  private readonly tournamentService = new TournamentService();

  role: AdminRole | null = null;
  publicOffline = false;
  loaded = false;
  isLoading = true;
  errorMessage = '';
  successMessage = '';
  isBulkActionRunning = false;
  isPublicStatusSaving = false;

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      const [authState, systemStatus] = await Promise.all([
        this.authService.me(),
        this.systemStatusService.get()
      ]);
      this.role = authState.role ?? null;
      this.publicOffline = Boolean(systemStatus.public_offline);
      this.loaded = true;
      this.errorMessage = '';
    } catch (error) {
      this.errorMessage = this.readError(error, 'Errore durante il caricamento delle operazioni.');
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  async resetAllMatches(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi azzeramento di risultati, stato e data di tutte le partite?')) {
      return;
    }
    await this.runBulkAction(() => this.matchService.resetAll(), 'Tutte le partite sono state azzerate.');
  }

  async deleteGroupMatches(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi la cancellazione di tutte le partite dei gironi?')) {
      return;
    }
    await this.runBulkAction(() => this.matchService.deleteGroupMatches(), 'Tutte le partite dei gironi sono state cancellate.');
  }

  async deleteKnockoutMatches(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi la cancellazione di tutte le partite della fase finale?')) {
      return;
    }
    await this.runBulkAction(() => this.matchService.deleteKnockoutMatches(), 'Tutte le partite della fase finale sono state cancellate.');
  }

  async deleteAllGroups(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi la cancellazione di tutti i gironi? L operazione riesce solo se non ci sono partite.')) {
      return;
    }
    await this.runBulkAction(() => this.tournamentService.deleteAllGroups(), 'Tutti i gironi sono stati cancellati.');
  }

  async deleteAllTeams(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi la cancellazione di tutte le squadre? L operazione riesce solo se non ci sono partite e gironi.')) {
      return;
    }
    await this.runBulkAction(() => this.teamService.deleteAll(), 'Tutte le squadre sono state cancellate.');
  }

  async deleteAllPlayerRegistry(): Promise<void> {
    if (this.isBulkActionRunning) {
      return;
    }

    if (!window.confirm('Confermi la cancellazione di tutte le anagrafiche giocatori? L operazione riesce solo se non ci sono giocatori associati alle squadre.')) {
      return;
    }
    await this.runBulkAction(() => this.playerRegistryService.deleteAll(), 'Tutte le anagrafiche giocatori sono state cancellate.');
  }

  async togglePublicOffline(): Promise<void> {
    if (this.isPublicStatusSaving) {
      return;
    }

    const nextState = !this.publicOffline;
    const message = nextState
      ? 'Confermi di mettere offline il sistema pubblico? Solo il super user potra accedere all area admin.'
      : 'Confermi di riattivare il sistema pubblico?';
    if (!window.confirm(message)) {
      return;
    }

    try {
      this.isPublicStatusSaving = true;
      this.successMessage = '';
      this.errorMessage = '';
      const status = await this.systemStatusService.update(nextState);
      this.publicOffline = Boolean(status.public_offline);
      this.successMessage = this.publicOffline ? 'Operazione conclusa. Sistema pubblico offline.' : 'Operazione conclusa. Sistema pubblico online.';
      this.errorMessage = '';
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante l aggiornamento dello stato pubblico.');
      this.changeDetector.detectChanges();
    } finally {
      this.isPublicStatusSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  canUseMassiveActions(): boolean {
    return this.role === 'super_user';
  }

  private async runBulkAction(action: () => Promise<void>, successMessage: string): Promise<void> {
    try {
      this.isBulkActionRunning = true;
      this.successMessage = '';
      this.errorMessage = '';
      await action();
      this.successMessage = `Operazione conclusa. ${successMessage}`;
      this.errorMessage = '';
      await this.refresh();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante l operazione massiva.');
      this.changeDetector.detectChanges();
    } finally {
      this.isBulkActionRunning = false;
      this.changeDetector.detectChanges();
    }
  }

  private readError(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }
}
